import { useEffect } from "react";
import loadingStore from "../stores/Loading/loadingStore";

/**
 * Hook that automatically registers/unregisters loading state with the central loading store
 * 
 * @param sourceName - Unique identifier for this loading source
 * @param isLoading - Loading state from React Query (or any other source)
 * @param enabled - Whether to track this loading state (defaults to true)
 */
export const useLoadingState = (
  sourceName: string, 
  isLoading: boolean, 
  enabled: boolean = true
) => {
  useEffect(() => {
    if (!enabled) return;

    // Register the current loading state
    loadingStore.setLoading(sourceName, isLoading);

    // Cleanup function to ensure we remove the loading source when component unmounts
    return () => {
      loadingStore.removeLoadingSource(sourceName);
    };
  }, [sourceName, isLoading, enabled]);

  // Also cleanup when sourceName changes (component re-mount with different source)
  useEffect(() => {
    return () => {
      if (enabled) {
        loadingStore.removeLoadingSource(sourceName);
      }
    };
  }, [sourceName, enabled]);
};

/**
 * Hook that provides manual control over loading states
 * Useful for non-React Query scenarios where you need to manually manage loading
 * 
 * @param sourceName - Unique identifier for this loading source
 * @returns Object with methods to manually control loading state
 */
export const useManualLoadingState = (sourceName: string) => {
  const setLoading = (isLoading: boolean) => {
    loadingStore.setLoading(sourceName, isLoading);
  };

  const clearLoading = () => {
    loadingStore.removeLoadingSource(sourceName);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      loadingStore.removeLoadingSource(sourceName);
    };
  }, [sourceName]);

  return {
    setLoading,
    clearLoading,
    isSourceLoading: loadingStore.isSourceLoading(sourceName),
  };
};